import { FilterControl } from ".";
import {
  DATA_SOURCE_LABEL_MAP,
  normalizeDollarBound,
  PREVIOUS_SALES_ALL,
  PREVIOUS_SALES_HAS,
  previousSalesState,
  SPECIALTY_COLOR_MAP,
  STATUS_COLOR_MAP,
} from "../consts";

/**
 * EIS-1093: coerce one end of a dollar range into a wire value.
 *
 * Returns `undefined` for "no bound on this end", which the caller OMITS from the
 * body. null, undefined and "" all mean no bound -- a cleared Mantine NumberInput
 * emits "", and `Number("")` is 0, so the obvious `Number(v)` one-liner is exactly
 * the bug this function exists to prevent.
 *
 * Non-numeric junk (a hand-edited or schema-drifted localStorage blob) also
 * degrades to "no bound" rather than NaN: NaN would serialise to null in JSON and
 * read as an unintended bound-shaped value server-side.
 *
 * EIS-1097 round 2: this DELEGATES to normalizeDollarBound rather than repeating
 * the coercion, because the repeated copy drifted -- it had the same
 * `Number(value)` bug that let `Number([]) === 0` manufacture a $0 bound out of
 * junk and let a coercion-hostile object throw. One rule, one place, and this
 * function is now only the null -> undefined adapter the omit-the-key caller
 * wants.
 */
const dollarBound = (value: unknown): number | undefined =>
  normalizeDollarBound(value) ?? undefined;

/**
 * Build the filter portion of the POST body for
 * /api/get_practitioners_for_chat_map.
 *
 * Extracted out of MapComponent.fetchPins by EIS-1088 so the wire contract is
 * unit-testable. The status / specialty / check-in behaviour is a verbatim move
 * (including sending `null` to deactivate a fully-selected multi-select and
 * leaving `undefined` when the control is missing) -- do not "tidy" it.
 *
 * EIS-1088 additions:
 *  - `data_source`: omitted when every source is selected (filter inactive), so
 *    the default body stays identical to the pre-EIS-1088 one. `[]` when nothing
 *    is selected, which the server short-circuits to zero pins.
 *  - `has_previous_sales`: see the EIS-1091 note below -- `true` | `false` |
 *    ABSENT, and nothing else.
 *
 * EIS-1097 (replacing EIS-1093's separate row):
 *  - `previous_sales_window_months`: 3 | 6 | 12 | 24, sent WITH
 *    `has_previous_sales` and never without it -- the window DEFINES what "has"
 *    and "no" mean, so a bare `has_previous_sales` would be an ambiguous
 *    question.
 *  - `min_previous_sales` / `max_previous_sales`: only under the Has segment,
 *    and only for a bound that is actually set. A blank maximum is OMITTED --
 *    it means "and up", never $0.
 *  - `min_rolling_12m_sales` / `max_rolling_12m_sales` are GONE from this body.
 *
 * EIS-1091 change:
 *  - `has_previous_sales` is derived from a THREE-WAY discriminant and can only
 *    be `true`, `false`, or absent. The old `[]` ("neither toggle selected" ->
 *    server short-circuits to zero pins) is GONE, and cannot be reintroduced
 *    from a stale blob either: previousSalesState() folds legacy arrays --
 *    including `[]` -- onto one of the three modes before we get here.
 *
 * A stale persisted `map-filters` blob has no dataSource/previousSales key at
 * all; `?? all` / previousSalesState() make that degrade to "all on" (inactive),
 * never to an empty map.
 */

export const buildMapFilterBody = (f?: FilterControl): { [k: string]: any } => {
  const filterBody: { [k: string]: any } = {};

  if (f?.status?.length === STATUS_COLOR_MAP.size) {
    // null to deactivate filter
    filterBody["status"] = null;
  } else {
    filterBody["status"] = f?.status?.map((s) => s.replace("BLANK", " - "));
  }

  if (f?.specialtyType?.length === SPECIALTY_COLOR_MAP.size) {
    // null to deactivate filter
    filterBody["specialty"] = null;
  } else {
    filterBody["specialty"] = f?.specialtyType?.map((s) => s.replace("BLANK", ""));
  }

  const allDataSources = Array.from(DATA_SOURCE_LABEL_MAP.keys());
  const selectedDataSources = f?.dataSource ?? allDataSources;
  if (selectedDataSources.length !== allDataSources.length) {
    filterBody["data_source"] = selectedDataSources;
  }

  // EIS-1091/1097: the merged Previous Sales filter. `has_previous_sales` is a
  // BOOLEAN or the key is ABSENT -- there is no third wire shape to produce, so
  // this filter alone can no longer ask the server for an empty map.
  //
  // previousSalesState() (not a bare read) is deliberate: buildMapFilterBody is
  // also called with UN-NORMALIZED input -- straight off localStorage in the
  // stale-blob tests, and by any future caller that forgets withFilterDefaults --
  // so an EIS-1088 array (including `[]`) and an EIS-1091 scalar must be folded
  // HERE too, not only in withFilterDefaults. Belt and braces, on the one
  // function that decides what the server is actually asked for.
  //
  // AC6: at rest (All) NOT ONE of these keys appears, so the default request is
  // byte-for-byte the pre-EIS-1088 one.
  const previousSales = previousSalesState(f?.previousSales);
  if (previousSales.mode !== PREVIOUS_SALES_ALL) {
    filterBody["has_previous_sales"] = previousSales.mode === PREVIOUS_SALES_HAS;
    // Sent for BOTH segments and never on its own: "No previous sales" is the
    // exact complement of "Has" INSIDE THIS WINDOW, so omitting the window would
    // leave the server answering a different question than the row describes.
    filterBody["previous_sales_window_months"] = previousSales.windowMonths;

    // The range narrows Has only. Under No it is not merely skipped -- the union
    // has no min/max to read, so "no previous sales between $1k and $5k" cannot
    // be constructed, let alone sent.
    if (previousSales.mode === PREVIOUS_SALES_HAS) {
      const min = dollarBound(previousSales.min);
      const max = dollarBound(previousSales.max);
      if (min !== undefined) {
        filterBody["min_previous_sales"] = min;
      }
      // A BLANK maximum is OMITTED, never sent as 0. Sending 0 would turn
      // "$5,000 and up" into "$0 - $5,000" -- the exact opposite set of
      // practitioners -- and would do it silently. See dollarBound().
      if (max !== undefined) {
        filterBody["max_previous_sales"] = max;
      }
      // ROUND 3, AND THIS IS THE POINT: a bound that is a REAL number the user
      // typed goes out EXACTLY as typed, even when the pair is
      // self-contradictory -- `max: 0` under Has ("sales > 0 AND sales <= 0"),
      // a negative maximum, or min > max. All three come back empty, correctly:
      // that IS the answer to the question asked.
      //
      // Do NOT add a clamp, a guard, or a "helpful" omission here. Dropping
      // `max: 0` would silently promote the query to "$1 and up" -- a different
      // question, whose pins would then contradict the range the row is
      // displaying, and the user would never know either happened.
      //
      // The problem those cases really had was SILENCE, and silence is fixed
      // where the user is: previousSalesImpossibleRange() in ../consts drives a
      // warning under the range inputs and a "no matches possible" marker on the
      // collapsed row, so an empty map is explained before it arrives rather
      // than left looking like an outage.
    }
  }

  return filterBody;
};

/**
 * EIS-1093: the shared shape of a "filter could not be applied" helper pair.
 *
 * Generalised so a second range filter does not clone the subtle bits
 * (transition-only firing, and "a missing response must LEAVE THE MEMORY
 * ALONE"). EIS-1088's exported functions keep their names, signatures and
 * behaviour exactly -- they just delegate here.
 *
 * EIS-1097: the map now has exactly ONE consumer of this pair. The generalisation
 * is kept because the abstraction is what stops the next filter from re-deriving
 * the auth-blip behaviour by hand, not because a second caller exists today.
 */
const shouldNotifyUnavailable = (
  flag: boolean | undefined,
  wasUnavailable: boolean,
): boolean => !!flag && !wasUnavailable;

const nextUnavailableState = (
  response: object | null | undefined,
  flag: boolean | undefined,
  wasUnavailable: boolean,
): boolean => (response ? !!flag : wasUnavailable);

/**
 * EIS-1088 (round 2): should we raise the "Previous Sales filter could not be
 * applied" notification for this map response?
 *
 * The server sets `previous_sales_unavailable` when the previous-sales filter was
 * requested but the keystats microservice was down, so it SKIPPED the sales
 * predicate rather than assert a falsehood about which accounts have bought. The
 * pins on screen are therefore a SUPERSET of what the user asked for and we must
 * NOT be silent about it.
 *
 * `wasUnavailable` is the flag's value from the previous response, so the toast
 * fires on the transition INTO the degraded state only -- a user who keeps
 * re-filtering or panning during an outage is not spammed with the same message
 * on every fetch, and recovery re-arms it for the next outage.
 */
export const shouldNotifyPreviousSalesUnavailable = (
  response: { previous_sales_unavailable?: boolean } | null | undefined,
  wasUnavailable: boolean,
): boolean => shouldNotifyUnavailable(response?.previous_sales_unavailable, wasUnavailable);

/**
 * The next value of the "was previously unavailable" memory.
 *
 * EIS-1088 (round 3): a MISSING response must LEAVE THE MEMORY ALONE.
 * `postData` resolves `undefined` when Auth0 reports unauthenticated, and
 * blindly assigning `!!response?.previous_sales_unavailable` in that case would
 * reset a live degraded state to false -- so the next flagged response during the
 * SAME keystats outage would read as a fresh false->true transition and re-toast
 * the user. Only a real response may clear the memory.
 */
export const nextPreviousSalesUnavailableState = (
  response: { previous_sales_unavailable?: boolean } | null | undefined,
  wasUnavailable: boolean,
): boolean => nextUnavailableState(response, response?.previous_sales_unavailable, wasUnavailable);

/** The user-facing copy for the notification above. Exported so the test pins the
 *  message actually shown, not a paraphrase of it. */
export const PREVIOUS_SALES_UNAVAILABLE_MESSAGE =
  "Previous Sales filter could not be applied — sales data is temporarily unavailable.";

/*
 * EIS-1097 REMOVED `shouldNotifyRolling12mSalesUnavailable`,
 * `nextRolling12mSalesUnavailableState` and ROLLING_12M_SALES_UNAVAILABLE_MESSAGE.
 *
 * With one row there is one skip to report, so there is ONE flag
 * (`previous_sales_unavailable`) and ONE toast. Keeping the second pair alive
 * would mean a single keystats outage could raise two toasts naming two filters
 * the UI no longer has -- and, worse, the server no longer sets
 * `rolling_12m_sales_unavailable` on this endpoint, so the second toast would be
 * dead code that silently never fires. The generalised helpers above stay
 * generalised: the sibling "Prior YTD Sales" ticket gets its pair the same way.
 */

/**
 * The check-in window keys, split out only because they need `todayPlusFormatted`
 * (a date helper) which the caller already owns. Kept verbatim from fetchPins.
 */
export const buildCheckInFilterBody = (
  f: FilterControl | undefined,
  todayPlusFormatted: (days: number) => string,
): { [k: string]: any } => {
  if (f?.maxDaysSinceLastCheckIn && f?.isCheckInFilterActive) {
    return {
      start_days_since_check_in: todayPlusFormatted(-f.maxDaysSinceLastCheckIn || 0),
      end_days_since_check_in: todayPlusFormatted(
        f?.minDaysSinceLastCheckIn ? -f?.minDaysSinceLastCheckIn + 1 : 1
      ),
    };
  }
  return {};
};
